/**
 * Everything the API documentation and the playground read, behind one interface
 * (`specs/M8-P4-developers-docs-playground.md` §6).
 *
 * Same pattern as the token page's `TokenSource`: a `source` prop with a tRPC default, so both
 * pages can be exercised in a test — and on `/__ui/developers-docs` — without a tRPC provider,
 * a session or a database, which is the only affordable way to assert a 1 300-type tree.
 */

export type ReferenceBadge = 'identical' | 'shape-ok' | 'deviation' | 'not-implemented' | 'untested';

export const BADGE_LABELS: Readonly<Record<ReferenceBadge, string>> = {
  identical: 'Identical to monday',
  'shape-ok': 'Same shape as monday',
  deviation: 'Documented deviation',
  'not-implemented': 'Not implemented',
  untested: 'Not covered by the corpus',
};

/** Short forms for the tree, where a row is 223 px wide. */
export const BADGE_SHORT: Readonly<Record<ReferenceBadge, string>> = {
  identical: 'identical',
  'shape-ok': 'shape',
  deviation: 'deviates',
  'not-implemented': 'not built',
  untested: 'untested',
};

export const REFERENCE_KINDS = [
  'query',
  'mutation',
  'object',
  'input',
  'interface',
  'union',
  'enum',
  'scalar',
] as const;
export type ReferenceKind = (typeof REFERENCE_KINDS)[number];

export const KIND_LABELS: Readonly<Record<ReferenceKind, string>> = {
  query: 'Queries',
  mutation: 'Mutations',
  object: 'Objects',
  input: 'Inputs',
  interface: 'Interfaces',
  union: 'Unions',
  enum: 'Enums',
  scalar: 'Scalars',
};

export interface ReferenceArgument {
  name: string;
  type: string;
  typeName: string;
  description: string | null;
  defaultValue: string | null;
  required: boolean;
  deprecationReason: string | null;
}

export interface ReferenceField {
  name: string;
  type: string;
  typeName: string;
  description: string | null;
  args: ReferenceArgument[];
  deprecationReason: string | null;
}

export interface ReferenceEnumValue {
  name: string;
  description: string | null;
  deprecationReason: string | null;
}

export interface ReferenceEntry {
  name: string;
  kind: ReferenceKind;
  description: string | null;
  summary: string;
  deprecationReason: string | null;
  signature: string | null;
  badge: ReferenceBadge | null;
  badgeNote: string | null;
  badgeOps: string[];
  args: ReferenceArgument[];
  fields: ReferenceField[];
  enumValues: ReferenceEnumValue[];
  possibleTypes: string[];
  interfaces: string[];
  example: string | null;
}

export interface ReferenceSummary {
  name: string;
  kind: ReferenceKind;
  summary: string;
  badge: ReferenceBadge | null;
  deprecated: boolean;
}

export interface ReferenceIndex {
  version: string;
  versions: string[];
  conformanceRunId: string;
  counts: Record<ReferenceKind, number>;
  badgeCounts: Record<ReferenceBadge, number>;
  entries: ReferenceSummary[];
}

export type GuideBlock =
  | { kind: 'p'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'code'; language: string; title: string | null; code: string }
  | { kind: 'table'; head: string[]; rows: string[][] }
  | { kind: 'note'; text: string };

export interface GuideSection {
  id: string;
  title: string;
  blocks: GuideBlock[];
}

export interface Guide {
  id: string;
  title: string;
  lede: string;
  sections: GuideSection[];
}

export interface DownloadLink {
  id: string;
  name: string;
  href: string;
  description: string;
}

export interface DevelopersOverview {
  baseUrl: string;
  endpoint: string;
  uploadEndpoint: string;
  tokenPage: string;
  versions: string[];
  defaultVersion: string;
  conformanceRunId: string;
  badgeCounts: Record<ReferenceBadge, number>;
  counts: Record<ReferenceKind, number>;
  downloads: DownloadLink[];
  publicDocs: boolean;
  canManagePublicDocs: boolean;
}

export interface PlaygroundExample {
  id: string;
  title: string;
  description: string;
  kind: 'read' | 'mutation';
  document: string;
  placeholders: string[];
}

export interface PlaygroundToken {
  id: string;
  name: string;
  lastUsedAt: string | null;
  /** `false` when the deployment keeps no copy of the value, so the proxy cannot run as it. */
  replayable: boolean;
}

export interface PlaygroundSetup {
  endpoint: string;
  proxyEndpoint: string;
  versions: string[];
  defaultVersion: string;
  credentialTtlSeconds: number;
  tokens: PlaygroundToken[];
  examples: PlaygroundExample[];
  /** Run first, with the playground's own credential, to fill the examples' placeholders. */
  idLookup: string;
}

export interface PlaygroundCredential {
  value: string;
  expiresAt: string;
  ttlSeconds: number;
}

export interface DevelopersSource {
  overview(): Promise<DevelopersOverview>;
  reference(version?: string): Promise<ReferenceIndex>;
  entry(name: string, version?: string): Promise<ReferenceEntry>;
  guides(version?: string): Promise<Guide[]>;
  playground(): Promise<PlaygroundSetup>;
  mintCredential(): Promise<PlaygroundCredential>;
  releaseCredential(): Promise<{ revoked: number }>;
  setPublicDocs(enabled: boolean): Promise<{ enabled: boolean }>;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The `{{placeholder}}` substitution, shared with the api (`playground.ts`).
 * ──────────────────────────────────────────────────────────────────────────── */

export const PLAYGROUND_PLACEHOLDERS = ['boardId', 'itemId', 'numbersColumnId', 'statusColumnId'] as const;
export type PlaygroundPlaceholder = (typeof PLAYGROUND_PLACEHOLDERS)[number];

export function fillPlaceholders(
  document: string,
  ids: Partial<Record<PlaygroundPlaceholder, string | null>>,
): string {
  let out = document;
  for (const name of PLAYGROUND_PLACEHOLDERS) {
    const value = ids[name];
    if (typeof value === 'string' && value.length > 0) out = out.split(`{{${name}}}`).join(value);
  }
  return out;
}

/**
 * What the id-lookup document answers, reduced to the four placeholders.
 *
 * Deliberately forgiving: a person with no readable board, or a board with no status column,
 * keeps the placeholder in the example rather than getting a document with `undefined` in it.
 */
export function idsFromLookup(data: unknown): Partial<Record<PlaygroundPlaceholder, string | null>> {
  const board = (
    (data as { boards?: unknown[] } | null | undefined)?.boards ?? []
  )[0] as
    | { id?: string; columns?: { id: string; type: string }[]; items_page?: { items?: { id: string }[] } }
    | undefined;
  if (!board?.id) return {};
  const columns = board.columns ?? [];
  const byType = (type: string): string | null => columns.find((column) => column.type === type)?.id ?? null;
  return {
    boardId: String(board.id),
    itemId: board.items_page?.items?.[0]?.id ? String(board.items_page.items[0].id) : null,
    numbersColumnId: byType('numbers'),
    statusColumnId: byType('status'),
  };
}
